import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

import routes from 'routes';
import { sendInvitation } from 'api/userApi';
import { getVisitorMembers } from 'api/visitorsApi';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import CustomConfirmModal from 'components/common/CustomConfirmModal';
import Tooltip from 'components/common/Tooltip';
import MemberAthletesCell from 'components/admin/MemberAthletesCell';

import useBulkSelection from 'hooks/useBulkSelection';
import useTableSort from 'hooks/useTableSort';
import useSessionFilters from 'hooks/useSessionFilters';
import BulkActionBar from 'components/admin/BulkActionBar';
import RosterRemovePreviewModal from 'components/admin/RosterRemovePreviewModal';

import styles from 'components/admin/bulkAdminTable.module.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const PORTAL_ACCESS_OPTIONS = [
  { value: 'all', label: 'All portal access' },
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Not invited', label: 'Not invited' },
];

const getDisplayName = (m) => {
  const fn = m?.firstName?.trim();
  const ln = m?.lastName?.trim();
  if (fn && ln) return `${fn} ${ln}`;
  if (fn) return fn;
  if (ln) return ln;
  return m?.email || 'Unknown';
};

const VisitorMembers = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const clubId = selectedClub?.clubID || '';

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);

  // Filters — persisted in sessionStorage so they survive navigate-away-and-back.
  const [filters, setFilter] = useSessionFilters(
    `visitorMembersFilters_${clubId}`,
    { searchText: '', meet: 'all', club: 'all', portalAccess: 'all', status: 'all' }
  );
  const {
    searchText,
    meet: meetFilter,
    club: clubFilter,
    portalAccess: portalAccessFilter,
    status: statusFilter,
  } = filters;

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [memberToInvite, setMemberToInvite] = useState(null);
  const [isResend, setIsResend] = useState(false);
  const [inviting, setInviting] = useState(false);

  // Bulk remove — change-preview model (§5.6): preview the cascade, confirm, apply.
  const [removeSeed, setRemoveSeed] = useState(null);

  const fetchMembers = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      const response = await getVisitorMembers(clubId);
      const enriched = (response.data || []).map((m) => ({
        ...m,
        name: getDisplayName(m),
        // Comma-joined athlete text drives sort + search; the cell itself renders type-badged chips.
        profiles: (m.athletes || [])
          .map((a) => `${a.firstName} ${a.lastName} (${a.age}${a.gender})`)
          .join(', '),
        meetNames: (m.meets || []).map((x) => x.meetName).join(', '),
        clubNames: (m.clubs || []).join(', '),
      }));
      setMembers(enriched);
    } catch (err) {
      console.error('[VisitorMembers] fetch error:', err);
      toast.error('Failed to load visitor members');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Meet filter options — distinct meets across all visitors, most recent first.
  const meetOptions = useMemo(() => {
    const byId = new Map();
    members.forEach((m) =>
      (m.meets || []).forEach((meet) => {
        if (!byId.has(meet.meetID)) byId.set(meet.meetID, meet);
      })
    );
    const opts = [{ value: 'all', label: 'All meets' }];
    Array.from(byId.values())
      .sort((a, b) => new Date(b.meetDate) - new Date(a.meetDate))
      .forEach((meet) => opts.push({ value: meet.meetID, label: meet.meetName }));
    return opts;
  }, [members]);

  // Club filter options — distinct representing clubs across all visitors.
  const clubOptions = useMemo(() => {
    const distinct = Array.from(
      new Set(members.flatMap((m) => m.clubs || []))
    ).sort((a, b) => a.localeCompare(b));
    const opts = [{ value: 'all', label: 'All clubs' }];
    distinct.forEach((c) => opts.push({ value: c, label: c }));
    return opts;
  }, [members]);

  // Family-aware search key: a secondary keys to its primary, everyone else to themselves — searching any family
  // member (primary, secondary, or athlete) surfaces the whole group.
  const familyKey = useCallback(
    (m) =>
      m.contactRole === 'SecondaryContact' && m.linkedPrimaryUserID
        ? m.linkedPrimaryUserID
        : m.userID,
    []
  );
  const familyHaystackByKey = useMemo(() => {
    const byKey = {};
    members.forEach((m) => {
      const text = `${m.name} ${m.email || ''} ${m.profiles || ''} ${m.meetNames || ''} ${m.clubNames || ''} ${m.linkedPrimaryName || ''}`.toLowerCase();
      byKey[familyKey(m)] = (byKey[familyKey(m)] || '') + ' ' + text;
    });
    return byKey;
  }, [members, familyKey]);

  // Filters. Status/portal/meet/club apply per member; search applies family-wide.
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (portalAccessFilter !== 'all' && m.invitationStatus !== portalAccessFilter) return false;
      if (statusFilter !== 'all' && m.participationStatus !== statusFilter) return false;
      if (meetFilter !== 'all' && !(m.meets || []).some((x) => x.meetID === meetFilter)) return false;
      if (clubFilter !== 'all' && !(m.clubs || []).includes(clubFilter)) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const hay = familyHaystackByKey[familyKey(m)] || '';
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [members, portalAccessFilter, statusFilter, meetFilter, clubFilter, searchText, familyHaystackByKey, familyKey]);

  // Sort
  const sortAccessors = useMemo(
    () => ({
      name: (r) => `${(r.lastName || '').trim()} ${(r.firstName || '').trim()}`.toLowerCase(),
      profiles: (r) => r.profiles,
      meetNames: (r) => r.meetNames,
      clubNames: (r) => r.clubNames,
      participationStatus: (r) => r.participationStatus,
      invitationStatus: (r) => r.invitationStatus,
    }),
    []
  );
  const { sortedItems, sortKey, sortDir, setSort } = useTableSort(filteredMembers, {
    defaultKey: 'name',
    accessors: sortAccessors,
  });

  // Selection
  const idAccessor = useCallback((m) => m.userID, []);
  const selection = useBulkSelection(sortedItems, idAccessor);

  // Group families for nested rendering (mirrors the Member/Guest lists).
  const presentUserIds = useMemo(
    () => new Set(sortedItems.map((m) => m.userID)),
    [sortedItems]
  );
  const isNestedChild = useCallback(
    (m) =>
      m.contactRole === 'SecondaryContact' &&
      !!m.linkedPrimaryUserID &&
      presentUserIds.has(m.linkedPrimaryUserID),
    [presentUserIds]
  );
  const childrenByPrimary = useMemo(() => {
    const map = {};
    sortedItems.forEach((m) => {
      if (isNestedChild(m)) {
        (map[m.linkedPrimaryUserID] = map[m.linkedPrimaryUserID] || []).push(m);
      }
    });
    return map;
  }, [sortedItems, isNestedChild]);
  const renderRows = useMemo(() => {
    const rows = [];
    sortedItems
      .filter((m) => !isNestedChild(m))
      .forEach((m) => {
        rows.push({ member: m, isChild: false });
        (childrenByPrimary[m.userID] || []).forEach((c) =>
          rows.push({ member: c, isChild: true })
        );
      });
    return rows;
  }, [sortedItems, childrenByPrimary, isNestedChild]);

  const handleInviteClick = (member, resend = false) => {
    if (member.participationStatus !== 'Active') {
      toast.error(
        'Cannot send invitation: Member status must be Active to receive invitations'
      );
      return;
    }
    setMemberToInvite(member);
    setIsResend(resend);
    setShowInviteModal(true);
  };

  const confirmInvitation = async () => {
    if (!memberToInvite) return;
    setInviting(true);
    try {
      await sendInvitation(clubId, memberToInvite.email);
      toast.success(
        isResend ? 'Invitation resent successfully' : 'Invitation sent successfully'
      );
      setShowInviteModal(false);
      setMemberToInvite(null);
      await fetchMembers();
    } catch (error) {
      const errorMessage =
        error?.response?.data || error?.message || 'Failed to send invitation';
      toast.error(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const sortIndicator = (key) =>
    sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '';

  return (
    <>
      <MetaTags title="SplitFast | Visitor Members" />
      <AdminTablePageLayout loading={loading}>
        <PinProtectedAction clubId={clubId}>
          {({ requestPinValidation }) => {
            const triggerWithPin = (actionType, onApproved, description) =>
              requestPinValidation(actionType, () => onApproved(), null, description);

            const bulkActions = [
              {
                label: 'Remove',
                variant: 'danger',
                onClick: () =>
                  triggerWithPin(
                    'bulk-remove-visitor-members',
                    () => setRemoveSeed({ userIds: selection.selectedIds }),
                    'The Admin PIN is required to remove visitor members from this club. Their portal accounts and any memberships in other clubs are preserved. Their meet registration history for this club is preserved. You will see exactly what will be removed — including their visitor athletes — before confirming.'
                  ),
              },
            ];

            return (
              <div className={styles.container}>
                <div className={styles.pageHeading}>
                  <div className={styles.pageTitle}>
                    Visitor Members
                    <span className={styles.countBadge}>
                      {members.length} {members.length === 1 ? 'Member' : 'Members'}
                    </span>
                  </div>
                </div>

                {/* Filter card */}
                <div className={styles.filterCard}>
                  <div className={`${styles.filterGroup} ${styles.filterGroupSearch}`}>
                    <label className={styles.filterLabel}>Search</label>
                    <input
                      className={styles.filterInput}
                      type="search"
                      placeholder="Search by name, email, athlete, meet, or club…"
                      value={searchText}
                      onChange={(e) => setFilter('searchText', e.target.value)}
                    />
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Meet</label>
                    <select
                      className={styles.filterSelect}
                      value={meetFilter}
                      onChange={(e) => setFilter('meet', e.target.value)}
                    >
                      {meetOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Club</label>
                    <select
                      className={styles.filterSelect}
                      value={clubFilter}
                      onChange={(e) => setFilter('club', e.target.value)}
                    >
                      {clubOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Portal Access</label>
                    <select
                      className={styles.filterSelect}
                      value={portalAccessFilter}
                      onChange={(e) => setFilter('portalAccess', e.target.value)}
                    >
                      {PORTAL_ACCESS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Status</label>
                    <select
                      className={styles.filterSelect}
                      value={statusFilter}
                      onChange={(e) => setFilter('status', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterResultCount}>
                    Showing <strong>{filteredMembers.length}</strong> of {members.length}
                  </div>
                </div>

                <BulkActionBar
                  count={selection.count}
                  itemLabel="member"
                  actions={bulkActions}
                  onClear={selection.clear}
                  allSelected={selection.allSelected}
                  someSelected={selection.someSelected}
                  onToggleAll={selection.toggleAll}
                />

                <div className={styles.tableCard}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                      <colgroup>
                        <col style={{ width: '44px' }} />
                        <col style={{ width: '20%' }} />
                        <col />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '170px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className={`${styles.checkboxCell} ${styles.notSortable}`}
                            aria-label="Select"
                          />

                          <th onClick={() => setSort('name')}>
                            Name / Email <span className={styles.sortIndicator}>{sortIndicator('name')}</span>
                          </th>
                          <th onClick={() => setSort('profiles')}>
                            Athletes <span className={styles.sortIndicator}>{sortIndicator('profiles')}</span>
                          </th>
                          <th onClick={() => setSort('meetNames')}>
                            Meets <span className={styles.sortIndicator}>{sortIndicator('meetNames')}</span>
                          </th>
                          <th onClick={() => setSort('clubNames')}>
                            Club <span className={styles.sortIndicator}>{sortIndicator('clubNames')}</span>
                          </th>
                          <th onClick={() => setSort('participationStatus')}>
                            Status <span className={styles.sortIndicator}>{sortIndicator('participationStatus')}</span>
                          </th>
                          <th onClick={() => setSort('invitationStatus')}>
                            Portal Access <span className={styles.sortIndicator}>{sortIndicator('invitationStatus')}</span>
                          </th>
                          <th className={styles.notSortable}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRows.length === 0 ? (
                          <tr className={styles.emptyRow}>
                            <td colSpan={8}>
                              {members.length === 0
                                ? 'No visitor members in this club.'
                                : 'No members match the current filters.'}
                            </td>
                          </tr>
                        ) : (
                          renderRows.map(({ member: m, isChild }) => {
                            const checked = selection.isSelected(m.userID);
                            const standaloneSecondary =
                              !isChild && m.contactRole === 'SecondaryContact';
                            const hasSecondary =
                              !isChild && (childrenByPrimary[m.userID]?.length > 0);
                            const inFamily = isChild || hasSecondary;
                            const rowClass = [
                              checked ? styles.rowSelected : '',
                              inFamily ? styles.familyRow : '',
                            ]
                              .filter(Boolean)
                              .join(' ');
                            return (
                              <tr key={m.userID} className={rowClass}>
                                <td className={styles.checkboxCell}>
                                  <input
                                    type="checkbox"
                                    aria-label={`Select ${m.name}`}
                                    checked={checked}
                                    onChange={() => selection.toggle(m.userID)}
                                  />
                                </td>
                                <td>
                                  {isChild && (
                                    <span className={styles.muted} style={{ marginRight: 6 }}>└</span>
                                  )}
                                  {m.name}
                                  {isChild && (
                                    <span className={styles.muted} style={{ marginLeft: 6, fontSize: 12 }}>
                                      · Secondary
                                    </span>
                                  )}
                                  {standaloneSecondary && (
                                    <span className={styles.muted} style={{ marginLeft: 6, fontSize: 12 }}>
                                      · Secondary{m.linkedPrimaryName ? ` · ${m.linkedPrimaryName}` : ''}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {isChild ? (
                                    <span className={styles.muted}>(same family)</span>
                                  ) : (
                                    <MemberAthletesCell athletes={m.athletes} nativeType="Visitor" />
                                  )}
                                </td>
                                <td>
                                  {isChild ? (
                                    <span className={styles.muted}>(same family)</span>
                                  ) : (m.meets || []).length > 0 ? (
                                    <div className={styles.pillList}>
                                      {m.meets.map((x) => (
                                        <span key={x.meetID} className={styles.pill}>
                                          {x.meetName}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className={styles.muted}>—</span>
                                  )}
                                </td>
                                <td>
                                  {isChild ? (
                                    <span className={styles.muted}>(same family)</span>
                                  ) : (m.clubs || []).length > 0 ? (
                                    <div className={styles.pillList}>
                                      {m.clubs.map((c) => (
                                        <span key={c} className={styles.pill}>
                                          {c}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className={styles.muted}>—</span>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`${styles.badge} ${m.participationStatus === 'Active' ? styles.badgeActive : styles.badgeInactive}`}
                                  >
                                    {m.participationStatus}
                                  </span>
                                </td>
                                <td>{m.invitationStatus}</td>
                                <td>
                                  <div className={styles.rowActions}>
                                    <Tooltip text="View member details">
                                      <Link
                                        to={routes.admin.members.showMember.url(clubId, m.email)}
                                        className={styles.iconLink}
                                      >
                                        View
                                      </Link>
                                    </Tooltip>
                                    {m.invitationStatus === 'Not invited' && (
                                      <Tooltip text="Send invitation email">
                                        <button
                                          className={`${styles.inlineBtn} ${styles.inlineBtnRed}`}
                                          onClick={() => handleInviteClick(m, false)}
                                        >
                                          Invite
                                        </button>
                                      </Tooltip>
                                    )}
                                    {m.invitationStatus === 'Pending' && (
                                      <Tooltip text="Resend invitation email">
                                        <button
                                          className={`${styles.inlineBtn} ${styles.inlineBtnWarning}`}
                                          onClick={() => handleInviteClick(m, true)}
                                        >
                                          Resend
                                        </button>
                                      </Tooltip>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          }}
        </PinProtectedAction>

        {/* Invite Modal */}
        {showInviteModal && (
          <CustomConfirmModal
            show={showInviteModal}
            title={isResend ? 'Resend Invitation' : 'Send Invitation'}
            content={
              <div>
                <p>
                  {isResend ? 'Resend invitation to ' : 'Send invitation to '}
                  <strong>{getDisplayName(memberToInvite)}</strong>?
                </p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.75rem', color: '#6b7280' }}>
                  An email will be sent asking them to confirm their email address
                  and set a password to access the Results Portal Website and Club
                  Connect App.
                </p>
              </div>
            }
            confirmLabel={isResend ? 'Resend Invitation' : 'Send Invitation'}
            loading={inviting}
            onConfirm={confirmInvitation}
            onCancel={() => {
              setShowInviteModal(false);
              setMemberToInvite(null);
            }}
          />
        )}

        {/* Bulk remove — change-preview (§5.6): preview the cascade, confirm, apply */}
        <RosterRemovePreviewModal
          show={!!removeSeed}
          clubId={clubId}
          seed={removeSeed}
          title="Remove Visitor Members"
          typeLabel="visitor"
          onClose={() => setRemoveSeed(null)}
          onApplied={() => {
            selection.clear();
            fetchMembers();
          }}
        />
      </AdminTablePageLayout>
    </>
  );
};

export default VisitorMembers;
